import { useCart } from '@/context/CartContext';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const logoUrl = '/img/logo.png';

const navLinks = [
  { label: 'Início', href: '#inicio', scrollTo: 'top' },
  { label: 'Catálogo', href: '#catalogo', scrollTo: 'catalogo' },
  { label: 'Sobre', href: '#sobre', scrollTo: 'sobre' },
  { label: 'Contato', href: '#contato', scrollTo: 'contato' },
];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalItems, setIsOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';
  const atTop = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, scrollTo: string) => {
    e.preventDefault();
    setMobileOpen(false);

    if (isHome) {
      // Já estamos na home — faz scroll direto
      const targetId = href.replace('#', '');
      if (scrollTo === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const tryScroll = (attempts: number) => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        if (attempts < 10) {
          requestAnimationFrame(() => tryScroll(attempts + 1));
        }
      };
      requestAnimationFrame(() => tryScroll(0));
    } else {
      // Estamos em outra página — navega pra home com state
      navigate('/', { state: { scrollTo } });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
        }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#inicio" onClick={(e) => handleNavClick(e, '#inicio', 'top')} className="flex items-center gap-2 shrink-0">
            <img
              src={logoUrl}
              alt="Maçã Verde"
              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
            />
            <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap transition-colors duration-300 ${atTop ? 'text-white' : 'text-primary-800'}`}>
              Maçã Verde
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href, link.scrollTo)}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${atTop ? 'text-white hover:text-white/80' : 'text-foreground-700 hover:text-primary-700'}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="#catalogo"
              onClick={(e) => handleNavClick(e, '#catalogo', 'catalogo')}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors whitespace-nowrap ${atTop ? 'border-white text-white hover:bg-white/10' : 'border-primary-400 text-primary-700 hover:bg-primary-50'}`}
            >
              Ver catálogo
            </a>
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              aria-label="Abrir carrinho"
            >
              <i className={`ri-shopping-bag-line text-lg transition-colors duration-300 ${atTop ? 'text-white' : 'text-foreground-700'}`} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-white text-[10px] font-bold">
                  {totalItems}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Right */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="relative w-10 h-10 shrink-0 flex items-center justify-center"
              aria-label="Abrir carrinho"
            >
              <i className={`ri-shopping-bag-line text-xl transition-colors duration-300 ${atTop ? 'text-white' : 'text-foreground-700'}`} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-white text-[10px] font-bold">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-10 h-10 flex items-center justify-center transition-colors duration-300 ${atTop ? 'text-white' : 'text-foreground-800'}`}
              aria-label="Menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70">
          <nav className="flex flex-col px-4 py-4 gap-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href, link.scrollTo)}
                className="text-sm font-medium text-foreground-700 hover:text-primary-700 transition-colors py-2"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#catalogo"
              onClick={(e) => handleNavClick(e, '#catalogo', 'catalogo')}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-primary-400 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors mt-2"
            >
              Ver catálogo
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;